/**
 * "AI usage" tab of the runtime System administration console — what THIS
 * application's AI has consumed for the organization the func-admin runs.
 *
 * Why it is not the builder's Billing & Credits page. That page answers "what
 * have I spent", keyed on one platform account. A func-admin is asking a
 * different question — what has my application consumed, across everyone who
 * used it — and the people in the answer may hold no platform account at all
 * (a project user invited from the Users screen is a row in the tenant
 * registry and nothing more). So the report groups on the RUN-TIME identity
 * the call was served to, which is recorded on every run-time row.
 *
 * Everything here is narrowed server-side to this project AND this
 * organization: an outside organization running the same application sees its
 * own people and nobody else's, the same boundary its AI configuration has.
 *
 * Credits, never dollars — the server strips provider cost before it leaves.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertTriangle, Loader2, RefreshCw, Wallet } from 'lucide-react'
import {
  getRuntimeCredits, getRuntimeUsage,
  type RuntimeCreditsView, type RuntimeUsageBucket, type RuntimeUsageReport,
} from '@/api/runtimeAi'

const card: React.CSSProperties = {
  border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px', background: '#fff',
}
const inp: React.CSSProperties = {
  padding: '7px 9px', border: '1px solid #cbd5e1', borderRadius: 6,
  fontSize: 14, background: '#fff', color: '#1f2937', outline: 'none',
}
const th: React.CSSProperties = {
  textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#64748b',
  padding: '6px 8px', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap',
}
const td: React.CSSProperties = {
  fontSize: 13.5, padding: '6px 8px', borderBottom: '1px solid #f1f5f9',
}
const num: React.CSSProperties = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }

/** Ranges a person actually asks for, rather than making them type two dates. */
const RANGES = [
  { days: 7, label: 'Last 7 days' },
  { days: 30, label: 'Last 30 days' },
  { days: 90, label: 'Last 90 days' },
  { days: 365, label: 'Last 12 months' },
]

const fmtCredits = (n: number) => n >= 100 ? Math.round(n).toLocaleString()
  : n >= 1 ? n.toFixed(2) : n.toFixed(4).replace(/0+$/, '').replace(/\.$/, '')
const fmtInt = (n: number) => (n || 0).toLocaleString()

/** A person as the application knows them — a project user has only a name. */
function personLabel(name: string, key: string): string {
  if (name && key && name !== key) return name
  return name || key || 'Unknown'
}

function Totals({ totals, days }: { totals: RuntimeUsageBucket; days: string }) {
  const cells = [
    { label: 'Credits used', value: fmtCredits(totals.credits) },
    { label: 'Questions answered', value: fmtInt(totals.calls) },
    { label: 'Tokens in', value: fmtInt(totals.input_tokens) },
    { label: 'Tokens out', value: fmtInt(totals.output_tokens) },
  ]
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10 }}>
      {cells.map((c) => (
        <div key={c.label} style={card}>
          <div style={{ fontSize: 13, color: '#64748b' }}>{c.label}</div>
          <div style={{ fontSize: 21, fontWeight: 600, color: '#1f2937', fontVariantNumeric: 'tabular-nums' }}>
            {c.value}
          </div>
          <div style={{ fontSize: 13, color: '#94a3b8' }}>{days}</div>
        </div>
      ))}
    </div>
  )
}

/** Horizontal bars beat a number column for "who/what is using this up". */
function Breakdown<T extends RuntimeUsageBucket>({ title, rows, name, empty }: {
  title: string
  rows: T[]
  name: (row: T) => string
  empty: string
}) {
  const max = Math.max(...rows.map((r) => r.credits), 0.0001)
  return (
    <div style={card}>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8, color: '#1f2937' }}>{title}</div>
      {rows.length === 0 ? (
        <div style={{ fontSize: 13.5, color: '#94a3b8' }}>{empty}</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>Name</th>
              <th style={{ ...th, textAlign: 'right' }}>Credits</th>
              <th style={{ ...th, textAlign: 'right' }}>Questions</th>
              <th style={{ ...th, width: '34%' }}>Share</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td style={td}>{name(r)}</td>
                <td style={num}>{fmtCredits(r.credits)}</td>
                <td style={num}>{fmtInt(r.calls)}</td>
                <td style={td}>
                  <div style={{ background: '#eef2ff', borderRadius: 4, height: 8 }}>
                    <div style={{
                      width: `${Math.max(2, (r.credits / max) * 100)}%`,
                      background: '#6366f1', height: 8, borderRadius: 4,
                    }} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

export function RuntimeAiUsagePanel({ projectId, ownerId }: { projectId: string; ownerId: string }) {
  const [report, setReport] = useState<RuntimeUsageReport | null>(null)
  const [credits, setCredits] = useState<RuntimeCreditsView | null>(null)
  const [days, setDays] = useState(30)
  // Explicit dates win over the rolling range; blank falls back to it.
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)

  const load = useCallback(() => {
    setLoading(true); setErr(null)
    const params = from ? { date_from: from, date_to: to || undefined } : { days }
    Promise.all([
      getRuntimeUsage(ownerId, projectId, params),
      getRuntimeCredits(ownerId, projectId).catch(() => null),
    ])
      .then(([u, c]) => { setReport(u); setCredits(c) })
      .catch((e) => setErr(errText(e) || 'Could not load AI usage'))
      .finally(() => setLoading(false))
  }, [ownerId, projectId, days, from, to])
  useEffect(() => { load() }, [load])

  const rangeLabel = useMemo(() => {
    if (!report) return ''
    return `${report.from} → ${report.to}`
  }, [report])

  const lowBalance = !!credits && credits.credit_billing_enabled && credits.on_credit
    && (credits.blocked !== null
      || (credits.low_balance_threshold !== null && credits.balance <= credits.low_balance_threshold))

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, color: '#1f2937' }}>
      <p style={{ margin: 0, fontSize: 14, color: '#64748b' }}>
        What this application's AI has used for <b>your organization</b> — every person who asked
        it something, whether or not they hold a Twasta account. Other organizations running this
        application have their own figures and are not counted here.
      </p>

      {credits && credits.on_credit && (
        <div style={{
          ...card,
          display: 'flex', alignItems: 'center', gap: 10,
          background: lowBalance ? '#fef2f2' : '#f8fafc',
          borderColor: lowBalance ? '#fecaca' : '#e2e8f0',
        }}>
          {lowBalance
            ? <AlertTriangle size={18} style={{ color: '#b91c1c', flexShrink: 0 }} />
            : <Wallet size={18} style={{ color: '#475569', flexShrink: 0 }} />}
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>
              {fmtCredits(credits.balance)} credits{' '}
              <span style={{ fontWeight: 400, color: '#64748b' }}>
                {credits.scope === 'tenant' ? "in your organization's balance" : "in the owner's balance"}
              </span>
            </div>
            <div style={{ fontSize: 13, color: lowBalance ? '#b91c1c' : '#64748b' }}>
              {credits.blocked?.detail
                || (credits.credit_billing_enabled
                  ? 'This application runs on AI Credit — every answer below was charged to this balance.'
                  : 'Credit billing is switched off on this instance, so nothing is being charged yet.')}
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <select style={inp} value={from ? '' : String(days)}
          onChange={(e) => { setFrom(''); setTo(''); setDays(Number(e.target.value)) }}>
          {RANGES.map((r) => <option key={r.days} value={r.days}>{r.label}</option>)}
          {from && <option value="">Custom dates</option>}
        </select>
        <span style={{ fontSize: 13, color: '#94a3b8' }}>or</span>
        <input style={inp} type="date" value={from} max={to || undefined}
          onChange={(e) => setFrom(e.target.value)} />
        <span style={{ fontSize: 13, color: '#94a3b8' }}>to</span>
        <input style={inp} type="date" value={to} min={from || undefined}
          onChange={(e) => setTo(e.target.value)} />
        {from && (
          <button style={{ ...inp, cursor: 'pointer' }} onClick={() => { setFrom(''); setTo('') }}>
            Clear
          </button>
        )}
        <button style={{ ...inp, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}
          onClick={load} disabled={loading}>
          <RefreshCw size={13} /> Refresh
        </button>
        {rangeLabel && <span style={{ fontSize: 13, color: '#94a3b8' }}>{rangeLabel}</span>}
      </div>

      {err && <div style={{ fontSize: 13.5, color: '#dc2626' }}>{err}</div>}

      {loading && !report ? (
        <div style={{ padding: 26, textAlign: 'center', color: '#64748b' }}>
          <Loader2 className="animate-spin" />
        </div>
      ) : report && (
        <>
          <Totals totals={report.totals} days={rangeLabel} />

          <Breakdown title="By person" rows={report.by_user}
            name={(r) => personLabel(r.user_name, r.user_key)}
            empty="Nobody has asked this application's AI anything in this period." />

          <Breakdown title="By functionality" rows={report.by_function}
            name={(r) => r.label}
            empty="No AI features have been used in this period." />

          <Breakdown title="By model" rows={report.by_model}
            name={(r) => `${r.model || 'default'}${r.provider ? ` · ${r.provider}` : ''}`}
            empty="No calls in this period." />

          {report.by_day.length > 0 && (
            <div style={card}>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>By day</div>
              <DailyBars rows={report.by_day} />
            </div>
          )}

          {report.recent.length > 0 && (
            <div style={card}>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
                Recent questions{' '}
                <span style={{ fontWeight: 400, fontSize: 13, color: '#94a3b8' }}>
                  (latest {report.recent.length})
                </span>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={th}>When</th>
                      <th style={th}>Who</th>
                      <th style={th}>What</th>
                      <th style={th}>Model</th>
                      <th style={{ ...th, textAlign: 'right' }}>Credits</th>
                      <th style={{ ...th, textAlign: 'right' }}>Tokens</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.recent.map((c, i) => (
                      <tr key={i}>
                        <td style={td}>{c.at ? new Date(c.at).toLocaleString() : ''}</td>
                        <td style={td}>{personLabel(c.user_name, c.user_key)}</td>
                        <td style={td}>{c.label}</td>
                        <td style={td}>{c.model || '—'}</td>
                        <td style={num}>
                          {c.on_credit ? fmtCredits(c.credits)
                            : <span style={{ color: '#94a3b8' }}>own key</span>}
                        </td>
                        <td style={num}>{fmtInt(c.input_tokens + c.output_tokens)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

/** A bar per day. Deliberately not a chart library — this console is embedded
 *  in the runtime shell and carries no charting dependency. */
function DailyBars({ rows }: { rows: { date: string; credits: number; calls: number }[] }) {
  const max = Math.max(...rows.map((r) => r.credits), 0.0001)
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 90 }}>
      {rows.map((r) => (
        <div key={r.date} title={`${r.date}: ${fmtCredits(r.credits)} credits, ${r.calls} questions`}
          style={{ flex: 1, minWidth: 4, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: '100%' }}>
          <div style={{
            height: `${Math.max(2, (r.credits / max) * 100)}%`,
            background: '#6366f1', borderRadius: '3px 3px 0 0',
          }} />
        </div>
      ))}
    </div>
  )
}

function errText(e: any): string {
  return e?.response?.data?.detail || e?.message || ''
}
